import { saveAs } from 'file-saver';

interface CalendarEvent {
  title: string;
  description: string;
  startTime: Date;
  endTime: Date;
  addAlarm?: boolean;
  alarmMinutesBefore?: number;
}

export const generateICSContent = (event: CalendarEvent): string => {
  // Format date to ICS format: YYYYMMDDTHHMMSSZ
  const formatDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d+/g, '');
  };

  const startDate = formatDate(event.startTime);
  const endDate = formatDate(event.endTime);

  // Escape special characters in description
  const escapedDescription = event.description.replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n');

  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${startDate}`,
    `DTEND:${endDate}`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${escapedDescription}`,
  ];

  // Add alarm if requested
  if (event.addAlarm && (event.alarmMinutesBefore !== undefined)) {
    // For 0 minutes, we use P0D (0 days) as the trigger
    const trigger = event.alarmMinutesBefore === 0
      ? 'TRIGGER:P0D'
      : `TRIGGER:-PT${event.alarmMinutesBefore}M`;

    icsLines.push(
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      `DESCRIPTION:Reminder: ${event.title}`,
      trigger,
      'END:VALARM'
    );
  }

  icsLines.push('END:VEVENT', 'END:VCALENDAR');

  return icsLines.join('\r\n');
};

// Generate ICS content for multiple events
const generateMultipleEventsICS = (events: CalendarEvent[]): string => {
  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  // Format date to ICS format: YYYYMMDDTHHMMSSZ
  const formatDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d+/g, '');
  };

  // Add each event
  events.forEach(event => {
    const startDate = formatDate(event.startTime);
    const endDate = formatDate(event.endTime);
    const escapedDescription = event.description.replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\n/g, '\\n');

    icsLines.push(
      'BEGIN:VEVENT',
      `DTSTART:${startDate}`,
      `DTEND:${endDate}`,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${escapedDescription}`
    );

    // Add alarm if requested
    if (event.addAlarm && (event.alarmMinutesBefore !== undefined)) {
      // For 0 minutes, we use P0D (0 days) as the trigger
      const trigger = event.alarmMinutesBefore === 0
        ? 'TRIGGER:P0D'
        : `TRIGGER:-PT${event.alarmMinutesBefore}M`;

      icsLines.push(
        'BEGIN:VALARM',
        'ACTION:DISPLAY',
        `DESCRIPTION:Reminder: ${event.title}`,
        trigger,
        'END:VALARM'
      );
    }

    icsLines.push('END:VEVENT');
  });

  icsLines.push('END:VCALENDAR');
  return icsLines.join('\r\n');
};

// Function to download a single event
export const downloadICSFile = (eventOrEvents: CalendarEvent | CalendarEvent[], filename?: string): void => {
  let icsContent: string;
  let defaultFilename: string;

  if (Array.isArray(eventOrEvents)) {
    // Multiple events
    icsContent = generateMultipleEventsICS(eventOrEvents);
    defaultFilename = 'calendar-events.ics';
  } else {
    // Single event
    icsContent = generateICSContent(eventOrEvents);
    defaultFilename = `${eventOrEvents.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
  }

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  saveAs(blob, filename || defaultFilename);
};

// Function to create a Google Calendar URL for an event
export const createGoogleCalendarEventUrl = (
  title: string,
  startTime: Date,
  endTime: Date,
  description: string = '',
  addAlarm?: boolean,
  alarmMinutesBefore?: number
): string => {
  // Format dates for Google Calendar URL (ISO format)
  // Keep the 'Z' to preserve UTC timezone information
  const formatGoogleDate = (date: Date) => {
    // Convert to ISO string, remove dashes and colons, remove milliseconds, but keep the 'Z'
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d+/g, '');
  };

  const startStr = formatGoogleDate(startTime);
  const endStr = formatGoogleDate(endTime);

  // Build the base URL with proper encoding
  let url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${startStr}/${endStr}&details=${encodeURIComponent(description)}`;

  // Add reminder if requested
  if (addAlarm && (alarmMinutesBefore !== undefined)) {
    // Try multiple approaches for Google Calendar reminders

    // Approach 1: Using popup and reminder parameters
    url += '&sf=true&output=xml';

    // Approach 2: Using the recur parameter
    url += `&recur=RRULE:FREQ=DAILY;COUNT=1`;

    // Approach 3: Using the add and popup parameters
    url += `&add=popup&popup=${alarmMinutesBefore}`;

    // Add a note about the reminder in the description
    const reminderNote = alarmMinutesBefore === 0
      ? "\n\nNotă: Adaugă o notificare la momentul începerii evenimentului."
      : `\n\nNotă: Adaugă o notificare cu ${alarmMinutesBefore} minute înainte de eveniment.`;

    url = url.replace(`&details=${encodeURIComponent(description)}`,
      `&details=${encodeURIComponent(description + reminderNote)}`);
  }

  return url;
};